// Éléments communs aux pages publiques de partage des ressources (liste et ressource seule)
import React from 'react';

export const DOMAINE_COLORS = {
  'Assurance':                        'bg-purple-100 text-purple-700',
  'banque':                           'bg-blue-100 text-blue-700',
  'conformité':                       'bg-red-100 text-red-700',
  'courtage':                         'bg-orange-100 text-orange-700',
  'courtage en assurances':           'bg-orange-100 text-orange-700',
  'courtage en prêts immobilier':     'bg-orange-100 text-orange-700',
  "création et gestion d'entreprise": 'bg-teal-100 text-teal-700',
  'crédits immo':                     'bg-cyan-100 text-cyan-700',
  'digital':                          'bg-indigo-100 text-indigo-700',
  'épargne':                          'bg-green-100 text-green-700',
  'fiscalité':                        'bg-yellow-100 text-yellow-700',
  'immobilier ancien':                'bg-stone-100 text-stone-700',
  'immobilier neuf':                  'bg-emerald-100 text-emerald-700',
  'immobilier sens large':            'bg-lime-100 text-lime-700',
  'jurisprudence et textes de lois':  'bg-rose-100 text-rose-700',
  'pédagogie':                        'bg-sky-100 text-sky-700',
  'Prévoyance':                       'bg-violet-100 text-violet-700',
  'prospection':                      'bg-pink-100 text-pink-700',
  'santé':                            'bg-green-100 text-green-700',
  'Solidarité':                       'bg-fuchsia-100 text-fuchsia-700',
  'vente et relation client':         'bg-amber-100 text-amber-700',
};

const PASTEL_POOL = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700',
  'bg-yellow-100 text-yellow-700', 'bg-pink-100 text-pink-700',
  'bg-indigo-100 text-indigo-700', 'bg-teal-100 text-teal-700',
];

const getColor = (opt) => DOMAINE_COLORS[opt] || PASTEL_POOL[opt.charCodeAt(0) % PASTEL_POOL.length];

export const majuscule = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const sansAccents = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Date affichée : date de publication si elle est plausible (une saisie « 0206 » existe), sinon date d'ajout
export const dateRessource = (r) => {
  const d = r.date_publication ? new Date(r.date_publication) : null;
  return d && d.getFullYear() >= 2000 ? d : new Date(r.created_at);
};

export const fmtDate = (d) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

// « moneyvox.fr » pour https://www.moneyvox.fr/…
export const sourceRessource = (r) => {
  if (!r.url) return null;
  try { return new URL(r.url).hostname.replace(/^www\./, ''); } catch { return null; }
};

// Premier vrai paragraphe du texte (saute signature, « Publié le… » et autres lignes du copier-coller)
export const extrait = (r) => {
  const lignes = (r.notes || '').split('\n').map(l => l.trim()).filter(l => l && l !== r.nom);
  const phrase = lignes.find(l => l.length >= 80 && /[.!?…»:]$/.test(l)) || lignes.find(l => l.length >= 80) || lignes[0] || '';
  return phrase.length > 320 ? phrase.slice(0, 320) : phrase;
};

export const BadgeNouveau = () => (
  <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200">
    Nouveau
  </span>
);

export const TagsDomaines = ({ domaines }) => (domaines || []).length > 0 && (
  <div className="flex flex-wrap gap-1.5">
    {domaines.map(d => (
      <span key={d} className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${getColor(d)}`}>{majuscule(d)}</span>
    ))}
  </div>
);

export const IconeLienExterne = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
);

export const EnTetePartage = ({ largeur = 'max-w-4xl' }) => (
  <header className="border-b border-gray-200 bg-white">
    <div className={`${largeur} mx-auto px-4 sm:px-6 py-3`}>
      <span className="text-sm font-semibold tracking-wide text-gray-600 uppercase">AXELERANCE Formation &amp; Conseil</span>
    </div>
  </header>
);

export const PageChargement = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" />
  </div>
);

export const PageLienInvalide = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="text-center">
      <p className="text-2xl font-bold text-gray-500 mb-2">Lien invalide ou révoqué</p>
      <p className="text-sm text-gray-500">Ce lien de partage n'existe plus ou a été désactivé. Demandez un nouveau lien à votre interlocuteur AXELERANCE.</p>
    </div>
  </div>
);

// Lecture d'une ressource : date, source, titre, thèmes, bouton vers l'original, puis le texte en paragraphes
export function ArticleRessource({ ressource, Titre = 'h1' }) {
  const source = sourceRessource(ressource);
  const paragraphes = (ressource.notes || '').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);

  return (
    <article>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 mb-3">
        {ressource.est_nouveau && <BadgeNouveau />}
        <span>{fmtDate(dateRessource(ressource))}</span>
        {source && <span>· {source}</span>}
      </div>
      <Titre className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-4">{ressource.nom}</Titre>
      {(ressource.domaines || []).length > 0 && <div className="mb-6"><TagsDomaines domaines={ressource.domaines} /></div>}
      {ressource.url && (
        <a href={ressource.url} target="_blank" rel="noopener noreferrer"
          className="mb-8 inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 transition">
          Lire sur {source || 'le site d’origine'}
          <IconeLienExterne />
        </a>
      )}
      {paragraphes.length > 0 ? (
        <div className="border-t border-gray-100 pt-6 text-[15px] leading-7 text-gray-800">
          {paragraphes.map((p, i) => <p key={i} className="mb-4 whitespace-pre-line">{p}</p>)}
        </div>
      ) : !ressource.url && (
        <p className="text-sm text-gray-500 italic">Aucun texte ni lien n'a été joint à ce sujet.</p>
      )}
    </article>
  );
}
